import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "./utils/supabaseClient";
import {
    Box,
    Button,
    Center,
    Container,
    Heading,
    Text,
    VStack,
    HStack,
    useToast,
    Spinner,
    Alert,
    AlertIcon,
    Badge,
    Divider,
    Flex,
    IconButton,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalCloseButton,
    useDisclosure
} from "@chakra-ui/react";
import { CheckCircleIcon, ArrowBackIcon, DownloadIcon, ViewIcon } from "@chakra-ui/icons";
import { FaFileAlt } from "react-icons/fa";
import { motion } from "framer-motion";
import moment from "moment";
import SignaturePad from 'react-signature-canvas';

export const CourseRegistrationForm = () => {
    const { registrationID } = useParams();
    const navigate = useNavigate();
    const toast = useToast();
    const [registrationData, setRegistrationData] = useState(null);
    const [allRegistrations, setAllRegistrations] = useState([]); // For full certifications
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [signaturePad, setSignaturePad] = useState(null);
    const [isSigned, setIsSigned] = useState(false);
    const [isFullCertification, setIsFullCertification] = useState(false);
    const { isOpen, onOpen, onClose } = useDisclosure();
    const { isOpen: isChecklistOpen, onOpen: onChecklistOpen, onClose: onChecklistClose } = useDisclosure();

    useEffect(() => {
        const fetchRegistration = async () => {
            setLoading(true);
            try {
                // First, fetch the specific registration with webhook_sent field
                const { data: singleRegistration, error: singleError } = await supabase
                    .from("full_registration_details_with_enrollment")
                    .select("*")
                    .eq("id", registrationID)
                    .single();

                // Note: webhook_sent column doesn't exist in database
                // Pipedream handles duplicate prevention by checking enrollment_email_isSent field

                if (singleError) throw singleError;
                setRegistrationData(singleRegistration);

                // Check if this is a full certification
                if (singleRegistration.full_certification_id) {
                    setIsFullCertification(true);
                    
                    // Fetch all registrations for this specific certification run
                    const { data: allRegistrations, error: allError } = await supabase
                        .from("full_registration_details_with_enrollment")
                        .select("*")
                        .eq("full_certification_id", singleRegistration.full_certification_id)
                        .eq("certification_run_id", singleRegistration.certification_run_id)
                        .eq("participant_email", singleRegistration.participant_email)
                        .order("course_type_code");

                    if (allError) throw allError;
                    
                    // Keep only this session's registrations: same participant, same run, and created recently
                    const cutoffMs = Date.now() - 2 * 60 * 1000; // 10 minutes window
                    const recentRegistrations = (allRegistrations || []).filter(reg => {
                        const created = reg.created_at ? new Date(reg.created_at).getTime() : 0;
                        return created >= cutoffMs;
                    });
                    setAllRegistrations(recentRegistrations);
                    console.log('Filtered recent registrations for full certification:', recentRegistrations);
                } else {
                    setIsFullCertification(false);
                    setAllRegistrations([singleRegistration]);
                }
                
                // Contract generation removed - using simple declaration instead
            } catch (error) {
                console.error("Error fetching registration:", error);
                toast({
                    title: "Error",
                    description: "Could not load registration details.",
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                });
            } finally {
                setLoading(false);
            }
        };

        fetchRegistration();
    }, [registrationID, toast]);
    


    const handleSignatureEnd = () => {
        console.log('Signature end triggered');
        if (signaturePad && !signaturePad.isEmpty()) {
            console.log('Signature is not empty, setting isSigned to true');
            setIsSigned(true);
        } else {
            console.log('Signature pad is empty or not available');
        }
    };

    const clearSignature = () => {
        if (signaturePad) {
            signaturePad.clear();
            setIsSigned(false);
        }
    };

    const getForm12Html = () => {
        const form12Template = `<!DOCTYPE html>
<html lang="en">
<style>
  body { font-family: Arial, sans-serif; margin: 2rem; line-height: 1.5; }
  h1, h2, h3 { color: #1f2937; }
  ul { margin-left: 1.5rem; }
  .section { margin-bottom: 1.5rem; }
  .signature { margin-top: 2rem; }
</style>
</head>
<body>


<h3 style="text-align: center;">FORM 12</h3>
<h3 style="text-align: center;">PRIVATE EDUCATION ACT (No. 21 of 2009)</h3>
<h3 style="text-align: center;">PRIVATE EDUCATION REGULATIONS</h3>

<h3 style="text-align: center;">ADVISORY NOTE TO STUDENTS</h2>
<p><em>This note is for a prospective student.</em></p>

<p>You are strongly encouraged to thoroughly research on the private education institution (PEI) conducting the course before signing up for any course. You should consider, for example, the reputation of the PEI, the teacher-student ratio of its classes, the qualifications of the teaching staff, and the course materials provided by the PEI.</p>

<p>By signing and returning the Student Contract (the "Contract"), you agree to the terms and conditions which will bind you and the PEI, if you accept the PEI's offer of a place in a course of study offered or provided by the PEI.</p>

<p>You should ask the PEI to allow you to read a copy of the Contract (with all blanks filled in and options selected) in both English and the official language of your home country, if necessary. For your own protection, you should review all the PEI's policies, and check carefully that you agree to all the terms of the Contract, including the details relating to each of the following sections, before signing the Contract:</p>

<ol>
  <li>The duration of the course, including holidays and examination schedules, and contact hours by days and week;</li>
  <li>The total fees payable, including course fees and other related costs;</li>
  <li>Dates when respective payments are due;</li>
  <li>The refund policy in the event of voluntary withdrawal (by you) or enforced dismissal from the course or programme (by PEI);</li>
  <li>The Fee Protection Scheme you are subscribed to and its coverage;</li>
  <li>The dispute resolution methods available; and</li>
  <li>Information about the PEI's policies on academic and disciplinary matters;</li>
  <li>The degree or diploma or qualification which will be awarded to you upon successful completion of the course.</li>
</ol>

<p>If you have any doubt about the contents of the Contract, or if the terms are different from what the agent or the PEI have informed you previously, or advertised, you should always seek advice and/or clarifications before signing the contract.</p>

<h3>To be completed by the signatory of the Student Contract</h3>

<p>This portion below is to be completed by the signatory of the Student Contract, i.e. either the student, or if the student is below the age of 18, his parent or guardian.</p>

<p>I, <strong>{{Name}}</strong>, NRIC/Passport number <strong>{{NRIC}}</strong>, have read and understood this advisory note before signing the Student Contract for myself/my ward.</p>

<p>
<strong>With:</strong> Edusphere College of Management and Technology Pte Ltd<br>
(name of PEI)
</p>

<div class="signature">
  <p style="border-top: 1px solid #000; width: 400px">(signature of student or parent / guardian)</p>
  <p>Date: {{today_date}}</p>
</div>

</body>
</html>`;

        // Replace placeholders with dynamic data
        return form12Template
            .replace('{{Name}}', registrationData?.participant_name || 'N/A')
            .replace('{{NRIC}}', registrationData?.nric || registrationData?.passport || 'N/A')
            .replace('{{today_date}}', moment().format('DD MMM YYYY'));
    };

    const getPreCourseCounsellingChecklistHtml = () => {
        const checklistTemplate = `<!doctype html>
<html>
<head>
  <meta charset='utf-8'>
  <title>pre-course-counselling-checklist</title>
  <style>
    body {
      font-family: system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif;
      line-height: 1.5;
      padding: 20px;
      max-width: 900px;
      margin: auto;
    }
    table {
      border-collapse: collapse;
      width: 100%;
      margin: 1rem 0;
    }
    th, td {
      border: 1px solid #ddd;
      padding: 8px;
      vertical-align: top;
    }
    h1, h2, h3 {
      margin-top: 1.5rem;
    }
    .meta {
      color: #666;
      font-size: 0.9rem;
      margin-bottom: 1rem;
    }
    .pagebreak {
      page-break-after: always;
    }
  </style>
</head>
<body>
  <h2>Pre-Course Counselling Checklist</h2>
  <p>&nbsp;</p>
  <p>Instructions</p>
  <p>Staff to brief and assist students/parents in accordance to this checklist at the point of inquiry and submitted together with course application form to Edusphere College Of Management And Technology.</p>
  <p>Edusphere College Of Management And Technology Manager will review information with student at point of admission and closed any information gaps or doubts prior to signing of student contract.</p>
  <p>Edusphere College Of Management And Technology will provide information at Orientation and closed any information gaps, clariﬁes any doubts, if necessary.</p>
  <p>Staff to tick the "Checked" box accordingly.</p>
  <p>Note: All information provided is treated with strictest conﬁdentiality and are meant for internal use only</p>
  <p>&nbsp;</p>
  <h3>SECTION A: APPLICANT'S PARTICULARS</h3>
  <p>Name of Applicant (as in NRIC/FIN/Passport): {{participant_name}} NRIC/FIN No./Passport No: {{nric_passport}}</p>
  <p>Mobile / Home Tel No: {{phone_number}}</p>
  <p>Email Address: {{email}}</p>
  <p>&nbsp;</p>
  <p>&nbsp;</p>
  <table>
    <tr>
      <td>
        <h4>SECTION B: CHECKLIST (Please turn over for Checklist)</h4>
      </td>
    </tr>
    <tr>
      <td>
        <h4>SECTION C: DECLARATION (To be completed at the end of counselling)</h4>
      </td>
    </tr>
    <tr>
      <td>
        <p>Staff/Agent: I conﬁrmed that the above have been explained to the student.</p>
      </td>
    </tr>
    <tr>
      <td>
        <p>Student: I understand fully what had been communicated to me and I hereby acknowledge that I have been briefed satisfactorily / not satisfactorily on the above.</p>
      </td>
    </tr>
  </table>
  <p>&nbsp;</p>
  <p>{{staff_name}} {{participant_name}}</p>
  <p>{{staff_date}} {{student_date}}</p>
  <p>Name, Signature of Staff & Date Signature of Student & Date</p>
  <p>&nbsp;</p>
  <p>&nbsp;</p>
  <p>FOR OFFICIAL USE ONLY</p>
  <p>Date Received</p>
  <p>Received by Programme Manager</p>
  <p>&nbsp;</p>
  <table>
    <tr>
      <td colspan="3">
        <h3>SECTION B: CHECKLIST (Explanation with the aid of website: )</h3>
      </td>
    </tr>
    <tr>
      <td><p>No.</p></td>
      <td><p>Items</p></td>
      <td><p>Checked</p></td>
    </tr>
    <tr><td><p>1</p></td><td><p>Brief school history, Vision, Mission, Values, Culture</p></td><td><p>✔</p></td></tr>
    <tr><td><p>2</p></td><td><p>School location, photograph or actual physical school premises</p></td><td><p>✔</p></td></tr>
    <tr><td><p>3</p></td><td><p>School physical facilities available</p></td><td><p>✔</p></td></tr>
    <tr><td><p>4</p></td><td><p>Size and number of classrooms</p></td><td><p>✔</p></td></tr>
    <tr><td><p>5</p></td><td><p>Organisation chart showing management team</p></td><td><p>✔</p></td></tr>
    <tr><td><p>6</p></td><td><p>Members of academic and examination boards</p></td><td><p>✔</p></td></tr>
    <tr><td><p>7</p></td><td><p>Teacher-student ratio</p></td><td><p>✔</p></td></tr>
    <tr><td><p>8</p></td><td><p>Reference to CPE website []</p></td><td><p>✔</p></td></tr>
    <tr><td><p>9</p></td><td><p>Course Pre-requisites, requirements and if any exemptions</p></td><td><p>✔</p></td></tr>
    <tr><td><p>10</p></td><td><p>Suitability of student to course</p></td><td><p>✔</p></td></tr>
    <tr><td><p>11</p></td><td><p>Opportunities for further education or job prospects after graduation</p></td><td><p>✔</p></td></tr>
    <tr><td><p>12</p></td><td><p>Course modules & outlines</p></td><td><p>✔</p></td></tr>
    <tr><td><p>13</p></td><td><p>Course duration and assessment schedules</p></td><td><p>✔</p></td></tr>
    <tr><td><p>14</p></td><td><p>Certification awarded, award criteria & appeals</p></td><td><p>✔</p></td></tr>
    <tr><td><p>15</p></td><td><p>Attendance Requirements</p></td><td><p>✔</p></td></tr>
    <tr><td><p>16</p></td><td><p>Application Requirements & Procedures</p></td><td><p>✔</p></td></tr>
    <tr><td><p>17</p></td><td><p>Payment methods, channels and Receipts</p></td><td><p>✔</p></td></tr>
    <tr><td><p>18</p></td><td><p>Total Payable fee throughout the course duration, Miscellaneous Fees</p></td><td><p>✔</p></td></tr>
    <tr><td><p>19</p></td><td><p>LONPAC Fee Protection Scheme / Industrial Wide Course Fee Protection Scheme</p></td><td><p>✔</p></td></tr>
    <tr><td><p>20</p></td><td><p>Medical Insurance Scheme</p></td><td><p>✔</p></td></tr>
    <tr><td><p>21</p></td><td><p>Late Payment & Refund Policy</p></td><td><p>✔</p></td></tr>
    <tr><td><p>22</p></td><td><p>Commitment to avoidance of over or under-charging</p></td><td><p>✔</p></td></tr>
    <tr><td><p>23</p></td><td><p>Transfer/Withdrawal/Deferment Policies</p></td><td><p>✔</p></td></tr>
    <tr><td><p>24</p></td><td><p>PEI Student Contract</p></td><td><p>✔</p></td></tr>
    <tr><td><p>25</p></td><td><p>CPE student Advisory Note</p></td><td><p>✔</p></td></tr>
    <tr><td><p>26</p></td><td><p>Orientation Programme</p></td><td><p>✔</p></td></tr>
    <tr><td><p>27</p></td><td><p>Student grievance procedure / Dispute resolution system</p></td><td><p>✔</p></td></tr>
    <tr><td><p>28</p></td><td><p>Student support services available</p></td><td><p>✔</p></td></tr>
    <tr><td><p>29</p></td><td><p>Feedback mechanism</p></td><td><p>✔</p></td></tr>
    <tr><td><p>30</p></td><td><p>Service Quality Targets</p></td><td><p>✔</p></td></tr>
    <tr><td><p>31</p></td><td><p>Confidentiality of student data</p></td><td><p>✔</p></td></tr>
    <tr><td><p>32</p></td><td><p>Quality assurance of delivery of course</p></td><td><p>✔</p></td></tr>
    <tr><td><p>33</p></td><td><p>Student Handbooks</p></td><td><p>✔</p></td></tr>
    <tr><td><p>34</p></td><td><p>Edusphere College website []</p></td><td><p>✔</p></td></tr>
  </table>
  <p>&nbsp;</p>
</body>
</html>`;

        // Replace placeholders with dynamic data
        const todayDate = moment().format('DD-MM-YYYY');
        return checklistTemplate
            .replace('{{participant_name}}', registrationData?.participant_name || 'N/A')
            .replace('{{participant_name}}', registrationData?.participant_name || 'N/A')
            .replace('{{nric_passport}}', registrationData?.nric || registrationData?.passport || 'N/A')
            .replace('{{phone_number}}', registrationData?.phone_number || registrationData?.mobile || 'N/A')
            .replace('{{email}}', registrationData?.participant_email || 'N/A')
            .replace('{{staff_name}}', 'Staff Name')
            .replace('{{staff_date}}', todayDate)
            .replace('{{student_date}}', todayDate);
    };

    const handleSubmit = async () => {
        if (!isSigned) {
            toast({
                title: "Signature Required",
                description: "Please sign the contract before submitting.",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        setSubmitting(true);
        try {
            // Get signature as base64
            console.log('Getting signature from pad...');
            const signature = signaturePad.getTrimmedCanvas().toDataURL('image/png');
            console.log('Signature captured, length:', signature.length);
            
            // Prepare timestamp
            const signedAt = moment().format('DD MMM YYYY HH:mm:ss');
            const signatureTimestamp = `Signed at: ${signedAt}`;
            const signatureTimestampIso = new Date().toISOString();

            if (isFullCertification) {
                // Update all registrations for full certification
                console.log('Updating all registrations for full certification:', allRegistrations.length);
                
                const updatePromises = allRegistrations
                    .filter(reg => reg.participant_email === registrationData.participant_email) // Extra safety check
                    .map(async (reg) => {
                        console.log('Updating registration:', reg.id, 'with signature for participant:', reg.participant_email);
                        
                        // Fetch current additional_data to preserve it
                        const { data: currentReg } = await supabase
                            .from('registration')
                            .select('additional_data')
                            .eq('id', reg.id)
                            .single();
                        
                        const updatedAdditionalData = {
                            ...(currentReg?.additional_data || {}),
                            legal_declaration_signature_url: signature,
                            legal_declaration_signature_timestamp: signatureTimestamp,
                            legal_declaration_signature_timestamp_iso: signatureTimestampIso
                        };

                        return supabase
                            .from('registration')
                            .update({ 
                                pre_registration_signature: signature,
                                additional_data: updatedAdditionalData
                            })
                            .eq('id', reg.id);
                    });
                
                const results = await Promise.all(updatePromises);
                const errors = results.filter(result => result.error);
                
                if (errors.length > 0) {
                    console.error('Some registrations failed to update:', errors);
                    throw new Error(`Failed to update ${errors.length} registrations`);
                }
                
                console.log('Successfully updated all registrations');
            } else {
                // Update single registration
                console.log('Updating single registration:', registrationID, 'with signature');
                
                // Fetch current additional_data to preserve it
                const { data: currentReg } = await supabase
                    .from('registration')
                    .select('additional_data')
                    .eq('id', registrationID)
                    .single();
                
                const updatedAdditionalData = {
                    ...(currentReg?.additional_data || {}),
                    legal_declaration_signature_url: signature,
                    legal_declaration_signature_timestamp: signatureTimestamp,
                    legal_declaration_signature_timestamp_iso: signatureTimestampIso
                };

                const { error: updateError } = await supabase
                    .from('registration')
                    .update({ 
                        pre_registration_signature: signature,
                        additional_data: updatedAdditionalData
                    })
                    .eq('id', registrationID);

                if (updateError) {
                    console.error('Error updating registration:', updateError);
                    throw updateError;
                }
                console.log('Successfully updated single registration');
            }

            // Send webhook to Pipedream after signature submission
            // This is a different webhook from the registration email (eoj5hzqkqmnzf55 vs eoeipnkwo0k2bnd)
            console.log('Sending signature submission webhook to Pipedream...');
            
            // Prepare webhook payload matching Pipedream expectations
            const webhookPayload = {
                // Basic registration info
                registration_id: registrationID,
                all_registration_ids: isFullCertification ? allRegistrations.map(reg => reg.id) : [registrationID],
                registration_type: registrationData.registration_type,
                
                // Pipedream checks course_type field to determine if full certification
                course_type: isFullCertification ? 'full_certification' : 'individual_course',
                
                // For full certifications, Pipedream needs these fields
                full_cert_type_code: isFullCertification ? (registrationData.full_certification_id || registrationData.full_cert_type_code || null) : null,
                certification_run_id: isFullCertification ? (registrationData.certification_run_id || registrationData.draft_cert_run_code || null) : null,
                
                // Additional helpful data
                participant_name: registrationData.participant_name,
                participant_email: registrationData.participant_email,
                registration_timestamp: new Date().toISOString(),
                signature_submitted: true,
                signature_submitted_at: new Date().toISOString()
            };
            
            console.log('Signature webhook payload:', webhookPayload);

            // Send webhook asynchronously (don't block UI)
            fetch("https://eoj5hzqkqmnzf55.m.pipedream.net", {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(webhookPayload),
            })
            .then(response => {
                if (response.ok) {
                    console.log('Signature webhook sent successfully');
                } else {
                    console.error('Signature webhook failed:', response.status);
                }
            })
            .catch(error => {
                console.error('Error sending signature webhook:', error);
            });

            toast({
                title: "Contract Submitted Successfully!",
                description: isFullCertification 
                    ? `Your registration for all ${allRegistrations.length} modules has been completed.`
                    : "Your registration has been completed.",
                status: "success",
                duration: 3000,
                isClosable: true,
            });

            // Redirect to course interest received page
            navigate(`/course-interest-received/${registrationID}`);
        } catch (error) {
            console.error('Error submitting contract:', error);
            toast({
                title: "Error",
                description: "Failed to submit contract. Please try again.",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setSubmitting(false);
        }
    };


    if (loading) {
        return (
            <Center minH="100vh" bg="gray.50">
                <Spinner size="xl" color="teal.500" />
            </Center>
        );
    }

    if (!registrationData) {
        return (
            <Center minH="100vh" bg="gray.50">
                <Text color="gray.600">Registration not found.</Text>
            </Center>
        );
    }

    return (
        <Box minH="100vh" bg="gray.50" py={8}>
            <Container maxW="container.xl">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                >
                    <VStack spacing={6}>
                        {/* Header */}
                        <Box
                            p={6}
                            borderRadius="lg"
                            w="100%"
                        >
                            <VStack spacing={4}>
                                <Heading size="sm" color="gray.800">
                                    Pre Registration Notice
                                </Heading>
                                
                        
                                        <Text fontSize="md">
                                        Please review the documents below and sign to complete your registration.
                                    </Text>
                                

                                
                            </VStack>
                        </Box>

                        {/* Full Certification Modules List */}
                        {/* {isFullCertification && allRegistrations.length > 0 && (
                            <Box
                                bg="white"
                                p={6}
                                borderRadius="lg"
                                boxShadow="md"
                                w="100%"
                            >
                                <VStack spacing={4}>
                                    <Heading size="md" color="gray.800">
                                        Program Modules
                                    </Heading>
                                    <Text fontSize="sm" color="gray.600" textAlign="center">
                                        You are registering for {allRegistrations.length} modules in this certification program:
                                    </Text>
                                    
                                    <VStack spacing={2} w="100%">
                                        {allRegistrations.map((reg, index) => (
                                            <Box
                                                key={reg.id}
                                                p={3}
                                                bg="blue.50"
                                                borderRadius="md"
                                                border="1px solid"
                                                borderColor="blue.200"
                                                w="100%"
                                            >
                                                <HStack justify="space-between">
                                                    <VStack align="start" spacing={1}>
                                                        <Text fontWeight="bold" color="blue.800">
                                                            Module {index + 1}: {reg.name}
                                                        </Text>
                                                        <Text fontSize="sm" color="blue.600">
                                                            {reg.course_type_code} • {moment(reg.start_date).format('DD MMM YYYY')} - {moment(reg.end_date).format('DD MMM YYYY')}
                                                        </Text>
                                                    </VStack>
                                                    <Badge colorScheme="green" variant="subtle">
                                                        Included
                                                    </Badge>
                                                </HStack>
                                            </Box>
                                        ))}
                                    </VStack>
                                </VStack>
                            </Box>
                        )} */}

                        {/* Declaration by Applicant */}
                        <Box
                            bg="white"
                            p={6}
                            borderRadius="lg"
                            boxShadow="md"
                            w="100%"
                        >
                            <VStack spacing={4}>

                            <Heading size="sm" color="gray.800" mb={4}>
                                Pre-registration documents to review
                            </Heading>
                            <Box
                                p={6}
                            >
                                <VStack spacing={3} align="stretch">
                                    
                                    {/* Form 12 Document Row */}
                                    <Flex
                                        align="center"
                                        p={4}
                                        border="1px solid"
                                        borderColor="gray.200"
                                        borderRadius="md"
                                        bg="white"
                                        justify="space-between"
                                    >
                                        <HStack spacing={3} flex="1" mr={4}>
                                            <Box color="blue.500" fontSize="24px">
                                                <FaFileAlt />
                                            </Box>
                                            <VStack align="start" spacing={0}>
                                                <Text fontWeight="semibold" fontSize="sm">
                                                    Form 12 - Advisory Note to Students
                                                </Text>
                                                <Text fontSize="xs" color="gray.600">
                                                    Private Education Act
                                                </Text>
                                            </VStack>
                                        </HStack>
                                        <Button
                                            size="xs"
                                            leftIcon={<ViewIcon />}
                                            onClick={onOpen}
                                            variant="outline"
                                        >
                                            View
                                        </Button>
                                    </Flex>

                                    {/* Pre-Course Counselling Checklist Row */}
                                    <Flex
                                        align="center"
                                        p={4}
                                        border="1px solid"
                                        borderColor="gray.200"
                                        borderRadius="md"
                                        bg="white"
                                        justify="space-between"
                                    >
                                        <HStack spacing={3} flex="1" mr={4}>
                                            <Box color="teal.500" fontSize="24px">
                                                <FaFileAlt />
                                            </Box>
                                            <VStack align="start" spacing={0}>
                                                <Text fontWeight="semibold" fontSize="sm">
                                                    Pre-Course Counselling Checklist
                                                </Text>
                                                <Text fontSize="xs" color="gray.600">
                                                    Course Information & Requirements
                                                </Text>
                                            </VStack>
                                        </HStack>
                                        <Button
                                            size="xs"
                                            leftIcon={<ViewIcon />}
                                            onClick={onChecklistOpen}
                                            variant="outline"
                                        >
                                            View
                                        </Button>
                                    </Flex>
                                </VStack>
                            </Box>

                            {/* Form 12 Modal */}
                            <Modal isOpen={isOpen} onClose={onClose} size="4xl" scrollBehavior="inside">
                                <ModalOverlay />
                                <ModalContent>
                                    <ModalHeader>Form 12 - Advisory Note to Students</ModalHeader>
                                    <ModalCloseButton />
                                    <ModalBody pb={6}>
                                        <Box 
                                            dangerouslySetInnerHTML={{ __html: getForm12Html() }}
                                            sx={{
                                                '& h3': {
                                                    color: 'gray.800',
                                                    marginBottom: '1rem'
                                                },
                                                '& p': {
                                                    marginBottom: '1rem'
                                                },
                                                '& ol': {
                                                    marginLeft: '1.5rem',
                                                    marginBottom: '1rem'
                                                },
                                                '& li': {
                                                    marginBottom: '0.5rem'
                                                }
                                            }}
                                        />
                                    </ModalBody>
                                </ModalContent>
                            </Modal>

                            {/* Pre-Course Counselling Checklist Modal */}
                            <Modal isOpen={isChecklistOpen} onClose={onChecklistClose} size="4xl" scrollBehavior="inside">
                                <ModalOverlay />
                                <ModalContent>
                                    <ModalHeader>Pre-Course Counselling Checklist</ModalHeader>
                                    <ModalCloseButton />
                                    <ModalBody pb={6}>
                                        <Box 
                                            dangerouslySetInnerHTML={{ __html: getPreCourseCounsellingChecklistHtml() }}
                                            sx={{
                                                '& h2, & h3, & h4': {
                                                    color: 'gray.800',
                                                    marginBottom: '0.75rem'
                                                },
                                                '& p': {
                                                    marginBottom: '0.5rem'
                                                },
                                                '& table': {
                                                    borderCollapse: 'collapse',
                                                    width: '100%',
                                                    margin: '1rem 0'
                                                },
                                                '& th, & td': {
                                                    border: '1px solid #ddd',
                                                    padding: '8px',
                                                    verticalAlign: 'top'
                                                }
                                            }}
                                        />
                                    </ModalBody>
                                </ModalContent>
                            </Modal>
                            </VStack>
                        </Box>

                        {/* Digital Signature Section */}
                        <Box
                            bg="white"
                            p={6}
                            borderRadius="lg"
                            boxShadow="md"
                            w="100%"
                        >
                            <VStack spacing={4}>
                                <Heading size="sm" color="gray.800">
                                    Signature
                                </Heading>
                                
                                <Text fontSize="sm" color="gray.600" textAlign="center">
                                    Please sign below to complete your registration contract
                                </Text>

                                <Box
                                    border="2px solid"
                                    borderColor={isSigned ? "green.300" : "gray.300"}
                                    borderRadius="md"
                                    p={4}
                                    bg="gray.50"
                                >
                                    <SignaturePad
                                        ref={(ref) => setSignaturePad(ref)}
                                        canvasProps={{
                                            className: "signatureCanvas",
                                            height: 200,
                                            width: 600,
                                            style: {
                                                border: '1px solid',
                                                borderColor: 'gray.300',
                                                borderRadius: 'md',
                                                backgroundColor: 'white'
                                            }
                                        }}
                                        onEnd={handleSignatureEnd}
                                    />
                                </Box>

                                <HStack spacing={4}>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={clearSignature}
                                        disabled={!isSigned}
                                    >
                                        Clear Signature
                                    </Button>
                                    
                                    {isSigned && (
                                        <HStack>
                                            <CheckCircleIcon color="green.500" />
                                            <Text fontSize="sm" color="green.600" fontWeight="medium">
                                                Signature captured
                                            </Text>
                                        </HStack>
                                    )}
                                </HStack>
                            </VStack>
                        </Box>

                        {/* Action Buttons */}
                        <Box
                            bg="white"
                            p={6}
                            borderRadius="lg"
                            boxShadow="md"
                            w="100%"
                        >
                            <VStack spacing={4}>
                                <Button
                                    colorScheme="teal"
                                    size="lg"
                                    onClick={handleSubmit}
                                    isLoading={submitting}
                                    loadingText="Submitting Contract..."
                                    isDisabled={!isSigned}
                                    w="100%"
                                >
                                    Submit Contract & Complete Registration
                                </Button>
                                
                                <Button
                                    variant="outline"
                                    leftIcon={<ArrowBackIcon />}
                                    onClick={() => navigate("/registration")}
                                    w="100%"
                                >
                                    Back to Registration
                                </Button>
                            </VStack>
                        </Box>
                    </VStack>
                </motion.div>
            </Container>
        </Box>
    );
};
